import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {QuestionComponentBase} from 'src/app/model/QuestionComponentBase';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {BlockComparator, TextBlock} from 'src/app/utils/text-compare';
import {environment} from 'src/environments/environment';
import {V3Dictation, V3DictationAnswerHolder} from '../../../model/v3/cspa';
import {QuestionService} from '../../../services/api/impl/rest/question.service';
import {LogsService} from "../../../../utils/services/logs.service";
import {AuthorizationServiceProvider} from 'src/app/auth_profile/services/authorization-service.provider';
import {QuestionActionRequest, QuestionActionRequestWithAnimation, QuestionActionRequestWithValue} from "../../../../model/prototypes";
import {Subscription} from "rxjs";
import {ShortcutService} from "../../../services/shortcut.service";

@Component({
    selector: 'app-dict',
    templateUrl: './dict.component.html',
    styleUrls: ['./dict.component.scss'],
    standalone: false
})
export class DictComponent extends QuestionComponentBase<V3DictationAnswerHolder, V3Dictation> implements OnInit, OnDestroy, AfterViewInit {

  summary: TextBlock[];
  score: number;
  isAnswerShown: boolean = false
  isSpanish: boolean = false
  spanishLetters = ['á', 'é', 'í', 'ó', 'ú', 'ü', 'ñ', '¿', '¡']
  private cursorPosition: Array<number>
  showEvents = ['click', 'keydown'];
  videoSource: String = null
  loading = true
  isMobileView: boolean = false;
  qaRequestStart: (QuestionActionRequest | QuestionActionRequestWithValue | QuestionActionRequestWithAnimation)[]
  qaRequestStop: (QuestionActionRequest | QuestionActionRequestWithValue)[]
  private shortcutButtonSubscription: Subscription;
  private mediaLoadTimeoutId?: ReturnType<typeof setTimeout>;
  @ViewChild('summaryModal', {static: true}) summaryModal: ModalComponent;
  @ViewChild('answer', {static: false, read: ElementRef}) answerTextarea: ElementRef

  private _videoPlayer: ElementRef<HTMLVideoElement>

  @ViewChild('videoPlayer', {static: false})
  get videoPlayer(): ElementRef<HTMLVideoElement> {
    return this._videoPlayer
  }

  set videoPlayer(videoPlayer: ElementRef<HTMLVideoElement>) {
    if (!videoPlayer) return
    this._videoPlayer = videoPlayer;
    this.sendRequest(this.qaRequestStart)
  }

  @Input() isExam: boolean
  @Output() audioProgressEmitter = new EventEmitter<number>()

  private _audioPlayer: ElementRef<HTMLVideoElement>

  @ViewChild('audioPlayer', {static: false})
  get audioPlayer(): ElementRef<HTMLVideoElement> {
    return this._audioPlayer
  }

  set audioPlayer(audioPlayer: ElementRef<HTMLVideoElement>) {
    if (!audioPlayer) return
    this._audioPlayer = audioPlayer;
    this.sendRequest(this.qaRequestStart)
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobileView = document.body.clientWidth < 768;
    if (this.isMobileView && this.loading) {
      this.loading = false;
    }
  }

  constructor(
    private questionService: QuestionService,
    private shortcut: ShortcutService,
    logs: LogsService,
    authProvider: AuthorizationServiceProvider) {
    super(logs, authProvider);
  }

  ngOnInit() {
    this.createQaRequests()
    this.shortcutButtonSubscription = this.shortcut.btnRequest.subscribe(btn => {
      btn === 'stop' && this.stop()
      btn === 'play' && this.play()
    })
    this.isSpanish = this._question.questionDef.path.startsWith('sp')
    this.questionSubscription = this.questionService.answerRequest.subscribe(sendAnswer => {
      sendAnswer &&
      sendAnswer['answerType'] === 'V3Dictation' &&
      sendAnswer['getNewAnswer'] === true &&
      this.getAnswerForSubmit()
    })
    this.videoSource = this.getMedia('.mp4');
    this.isMobileView = document.body.clientWidth < 768;
    this.loading = !this.isMobileView;
    this.logs.log(`[Dict] init isMobileView=${this.isMobileView} audio=${this.getAudioSource()} video=${this.videoSource}`);
    if (this.loading) {
      this.mediaLoadTimeoutId = setTimeout(() => {
        if (!this.loading) return;
        this.logs.log(`[Dict] media load timeout, hiding spinner. audio=${this.getAudioSource()} video=${this.videoSource}`);
        this.loading = false;
      }, 10000);
    }
  }

  ngAfterViewInit() {
    this.showEvents.forEach((event) => {
      this.answerTextarea?.nativeElement.addEventListener(event, () => {
        setTimeout(() => {
          this.setCursorPosition()
        }, 1);
      });
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.destroyPlayers();
    this.summaryModal.hide()
    if (this.shortcutButtonSubscription) this.shortcutButtonSubscription.unsubscribe()
    if (this.mediaLoadTimeoutId) {
      clearTimeout(this.mediaLoadTimeoutId);
      this.mediaLoadTimeoutId = undefined;
    }
  }

  sendRequest(qaRequest: (QuestionActionRequest | QuestionActionRequestWithValue)[]) {
    this.updateActionMenu.emit(qaRequest)
  }

  play() {
    if (this.audioPlayer) {
      this.audioPlayer.nativeElement.play().then(_ => this.sendRequest(this.qaRequestStop))
    }
    if (this.videoPlayer && this.videoSource)
      this.videoPlayer.nativeElement.play().then(_ => this.sendRequest(this.qaRequestStop))
  }

  stop() {
    if (this.audioPlayer) this.audioPlayer.nativeElement.pause()
    if (this.videoPlayer && this.videoSource) this.videoPlayer.nativeElement.pause()
    this.sendRequest(this.qaRequestStart)
  }

  private destroyPlayers() {
    if (this.audioPlayer?.nativeElement) {
      const audio = this.audioPlayer.nativeElement;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }

    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  }

  updateProgress(data: any) {
    if(data.target.currentTime == data.target.duration)
      this.audioProgressEmitter.emit(-1)
    this.audioProgressEmitter.emit(data.target.currentTime/data.target.duration * 100)
  }

  getAnswerForSubmit(){
      this.isExam ? this.submitAnswer() : this.openDictationDialog();
  }

  getScore() {
    return this.score;
  }

  mayCorrect() {
    return this.getScore() !== 100
  }

  getSummary() {
    return this.summary
  }

  closeHostContract() {
    this.summaryModal.hide();
  }

  submitAnswer() {
    this.summaryModal.hide()
    this.answer = this._question.questionDef.answerDef.answerHolder as V3DictationAnswerHolder
    this.sendAnswer()
  }

  openDictationDialog() {
    this.prepareSummaryReport();
    this.summaryModal.show();
  }

  prepareSummaryReport() {
    const questionDef = this.question.questionDef as V3Dictation
    const holder = questionDef.answerDef.answerHolder as V3DictationAnswerHolder
    const correctAnswerStream = BlockComparator.tokenize(questionDef.answerDef.answer);
    const userAnswer = BlockComparator.tokenize(holder?.answer ?? '');
    const result = BlockComparator.compare(correctAnswerStream, userAnswer);
    this.score = Math.round((1 - (result[result.length - 1].score / correctAnswerStream.content.length)) * 100);
    const blocks = BlockComparator.divideToBlocks(result, userAnswer);
    this.summary = BlockComparator.compressBlocks(blocks);
  }

  getMedia(suffix: string) {
    if (!this.question) {
      return null;
    }
    const questionDef = this.question.questionDef as V3Dictation
    return environment.awsBase + questionDef.src + suffix;
  }

  setUpAnswer(answer: V3DictationAnswerHolder) {
    this.logs.log("setting up dictation answer ", JSON.stringify(answer))
    this.answer = answer ? answer : { answer: '', passScore: 0 };
    if (!this._question.questionDef.answerDef.answerHolder) {
      this._question.questionDef.answerDef.answerHolder = { answer: '', passScore: 0 }
    }
  }

  getAudioSource() {
    return this.getMedia('.mp3');
  }

  getAnswer() {
    const questionDef = this._question.questionDef as V3Dictation
    return questionDef.answerDef.answer
  }

  toggleAnswer() {
    this.isAnswerShown = !this.isAnswerShown
  }

  setCursorPosition() {
    let start = this.answerTextarea.nativeElement.selectionStart;
    let end = this.answerTextarea.nativeElement.selectionEnd;
    this.cursorPosition = [start, end];
  }

  addLetter(letter: string) {
    if(!this.cursorPosition)
      this.cursorPosition = [0, 0]
    let start = this.cursorPosition[0] != 0? this.cursorPosition[0]  : 0
    let end = this.cursorPosition[1] != 0? this.cursorPosition[1] : 0
    let currentValue = this.answerTextarea.nativeElement.value;
    const holder = this._question.questionDef.answerDef.answerHolder as V3DictationAnswerHolder
    holder.answer = currentValue.substr(0, start) + letter + currentValue.substr(end);
    this.answerTextarea.nativeElement.focus()
    setTimeout(() => {
      this.answerTextarea.nativeElement.setSelectionRange(this.cursorPosition[0] + 1, this.cursorPosition[1] + 1)
      this.setCursorPosition()
    }, 1);
  }

  errorMessageVideo() {
    this.logs.log(`[Dict] video load error. fallback to audio. video=${this.videoSource}`);
    this.videoSource = null
    this.loading = false
  }

  errorMessageAudio() {
    this.logs.log(`[Dict] audio load error. audio=${this.getAudioSource()}`);
    this.loading = false;
  }

  onMediaLoaded(kind: string) {
    this.logs.log(`[Dict] media loaded kind=${kind}`);
    this.loading = false;
    if (this.mediaLoadTimeoutId) {
      clearTimeout(this.mediaLoadTimeoutId);
      this.mediaLoadTimeoutId = undefined;
    }
  }

  get dictAnswerHolder(): V3DictationAnswerHolder {
    return this.question?.questionDef?.answerDef?.answerHolder as V3DictationAnswerHolder
  }

  private createQaRequests() {
    this.qaRequestStart = [
      {
        text: 'play',
        assetUrl: 'play-arrow-filled',
        animation: 'pulsating'
      }
    ]
    if (this.isExam) {
      this.qaRequestStop = [
        {
          text: '',
          assetUrl: 'play-arrow-disabled'
        }
      ]
    }
    else {
      this.qaRequestStop = [
        {
          text: 'stop',
          assetUrl: 'pause'
        }
      ]
    }
  }
}
